import { authenticatedFetch } from './authenticatedFetch';
import type { SheetDocumentData } from '../types/sheetDocument';

export type ApiErrorBody = {
  error?: string;
  message?: string;
  traceback?: string;
};

export type ApiError = {
  status: number;
  code: string;
  message: string;
  traceback?: string;
};

export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: ApiError };

export type CreateFreeAccountResponse = {
  user_id: string;
  account_id: string;
  document_id: string;
};

export type CreateDocumentRequest = {
  account_id: string;
  user_id: string;
  anonymous_user_id?: string;
  anonymous_account_id?: string;
};

export type CreateDocumentResponse = {
  id: string;
};

export type SaveDocumentRequest = {
  document_id: string;
  account_id: string;
  data: SheetDocumentData;
  new_taccounts?: Array<{
    taccount_id: string;
    name: string;
    alias?: string;
  }>;
  deleted_taccount_ids?: string[];
  anonymous_user_id?: string;
  anonymous_account_id?: string;
};

export type SaveDocumentResponse = {
  id: string;
  updated_at: string | null;
  account_was_linked: boolean;
};

export type LoadDocumentRequest = {
  document_id: string;
  account_id: string;
  anonymous_user_id?: string;
  anonymous_account_id?: string;
};

export type LoadDocumentResponse = {
  id: string;
  data: SheetDocumentData;
  updated_at: string | null;
  account_was_linked: boolean;
  is_read_only?: boolean;
};

export type GetDocumentsRequest = {
  account_id: string;
  anonymous_user_id?: string;
  anonymous_account_id?: string;
};

export type DocumentSummary = {
  id: string;
  created_at: string | null;
  updated_at: string | null;
  title?: string | null;
};

export type GetDocumentsResponse = {
  documents: DocumentSummary[];
  account_was_linked: boolean;
};

export type DeleteDocumentResponse = {
  success: boolean;
};

export type TAccountRecord = {
  taccount_id: string;
  account_id: string;
  name: string;
  alias: string | null;
};

export type GetTAccountsResponse = {
  taccounts: TAccountRecord[];
};

export type GetTAccountsRequest = {
  account_id: string;
};

export type CreateTAccountRequest = {
  account_id: string;
  name: string;
  alias?: string;
};

export type UpdateTAccountRequest = {
  account_id: string;
  taccount_id: string;
  name: string;
  alias?: string;
};

export type DeleteTAccountResponse = {
  success: boolean;
};

export type SendFeedbackRequest = {
  title: string;
  feedback: string;
  allow_contact: boolean;
};

export type SendFeedbackResponse = {
  success: boolean;
  issue_url: string;
  issue_number: number;
};

export type UserSettingsRequest = {
  account_id?: string;
  anonymous_user_id?: string;
  anonymous_account_id?: string;
};

export type UserSettingsPayload = {
  ui: {
    max_taccounts_per_row: number;
    show_decimals: boolean;
    format_numbers_with_commas: boolean;
    theme_mode: 'light' | 'dark';
  };
  export: {
    entries_separate_tab: boolean;
    format_numbers_with_commas: boolean;
    max_taccounts_per_row: number;
  };
};

export type GetUserSettingsResponse = {
  user_id: string;
  account_id: string;
  settings: UserSettingsPayload;
  account_was_linked: boolean;
};

export type SaveUserSettingsRequest = UserSettingsRequest & {
  settings: UserSettingsPayload;
};

export type SaveUserSettingsResponse = {
  user_id: string;
  account_id: string;
  settings: UserSettingsPayload;
  updated_at: string | null;
  account_was_linked: boolean;
};

export type GetAdminStatusResponse = {
  is_admin: boolean;
};

export type RuntimeFlags = {
  require_whitelist_cookie: boolean;
};

export type GetRuntimeFlagsResponse = {
  flags: RuntimeFlags;
};

export type UpdateRuntimeFlagsRequest = {
  flags: RuntimeFlags;
};

export type UpdateRuntimeFlagsResponse = {
  flags: RuntimeFlags;
};

export type BillingPlan = {
  plan_code: 'free' | 'pro' | string;
  display_name: string;
  limits: {
    max_taccounts_per_document: number;
    max_transactions_per_document: number;
    max_documents: number;
  };
  pricing: {
    currency: string;
    monthly_amount_cents: number;
    yearly_amount_cents: number;
  };
  free_trial_days?: number;
  is_current_plan: boolean;
};

export type AdminPlan = {
  plan_code: 'free' | 'pro' | string;
  display_name: string;
  max_documents: number;
  max_taccounts_per_document: number;
  max_transactions_per_document: number;
  month_price_usd: number;
  year_price_usd: number;
  free_trial_days: number;
};

export type GetAdminPlansResponse = {
  plans: AdminPlan[];
};

export type AdminUser = {
  user_id: string;
  name: string;
  email: string | null;
  plan: string;
  document_count: number;
  last_document_edited_at: string | null;
  is_admin: boolean;
};

export type GetAdminUsersResponse = {
  users: AdminUser[];
  page: number;
  page_size: number;
  total: number;
};

export type DeleteAdminUserResponse = {
  success: boolean;
  user_id: string;
};

export type UpdateAdminPlansRequest = {
  plans: Array<{
    plan_code: 'free' | 'pro' | string;
    max_documents: number;
    max_taccounts_per_document: number;
    max_transactions_per_document: number;
    month_price_usd: number;
    year_price_usd: number;
    free_trial_days: number;
  }>;
};

export type UpdateAdminPlansResponse = {
  plans: AdminPlan[];
};

export type BillingPlansRequest = {
  account_id?: string;
  anonymous_user_id?: string;
  anonymous_account_id?: string;
};

export type BillingPlansResponse = {
  current_plan_code: string;
  plans: BillingPlan[];
  checkout_available: boolean;
  portal_available: boolean;
};

export type BillingStatusRequest = BillingPlansRequest;

export type BillingStatusResponse = {
  plan_code: string;
  display_name: string;
  limits: {
    max_taccounts_per_document: number;
    max_transactions_per_document: number;
    max_documents: number;
  };
  pricing: {
    currency: string;
    monthly_amount_cents: number;
    yearly_amount_cents: number;
  };
  is_paid_plan: boolean;
  subscription_status: string;
  billing_interval: string | null;
  cancel_at_period_end: boolean;
  next_charge_at: string | null;
  checkout_available: boolean;
  portal_available: boolean;
};

export type CreateBillingCheckoutSessionRequest = {
  billing_interval: 'month' | 'year';
  anonymous_user_id?: string;
  anonymous_account_id?: string;
  discount_code?: string;
};

export type CreateBillingCheckoutSessionResponse = {
  session_id: string;
  checkout_url: string;
  trial_applied?: boolean;
  discount_applied?: boolean;
  trial_days?: number;
};

export type ValidateDiscountCodeResponse = {
  code: string;
  trial_days: number;
};

export type AdminDiscountCode = {
  discount_code_id: number;
  code: string;
  trial_days: number;
  commission_usd: number;
  ends_on: string | null;
  is_enabled: boolean;
  is_deleted: boolean;
  content_provider_id: number;
  provider_name: string;
  provider_email: string;
  new_users: number;
  created_at: string;
  updated_at: string;
};

export type AdminDiscountCodeRequest = {
  code: string;
  trial_days: number;
  commission_usd: number;
  ends_on: string | null;
  is_enabled: boolean;
  provider_name: string;
  provider_email: string;
};

export type AdminDiscountCodesResponse = { codes: AdminDiscountCode[] };

export type AdminCommissionProvider = {
  content_provider_id: number;
  name: string;
  email: string;
  unpaid_count: number;
  unpaid_total_usd: number;
  unpaid_commission_ids: number[];
};

export type AdminCommissionPaymentBatch = {
  commission_payment_batch_id: number;
  content_provider_id: number;
  provider_name: string;
  provider_email: string;
  commission_count: number;
  total_usd: number;
  paid_at: string;
  paid_by: string;
  commissions: Array<{
    discount_commission_id: number;
    code: string;
    account_id: string;
    amount_usd: number;
    earned_at: string;
  }>;
};

export type AdminCommissionsResponse = {
  providers: AdminCommissionProvider[];
  payment_batches: AdminCommissionPaymentBatch[];
};

export type CancelBillingSubscriptionRequest = {
  anonymous_user_id?: string;
  anonymous_account_id?: string;
};

export type CancelBillingSubscriptionResponse = {
  account_id: string;
  subscription_status: string;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  projected_plan: string;
  payment_methods_detached: number;
  payment_method_detach_failures: string[];
};

export type BillingDiagnosticCheck = {
  id: string;
  label: string;
  passed: boolean;
  detail: string;
};

export type BillingDiagnosticsResponse = {
  summary: {
    ready_for_uat: boolean;
    checked_at: string;
  };
  checks: BillingDiagnosticCheck[];
  config: {
    checkout_available: boolean;
    portal_available: boolean;
    stripe_secret_configured: boolean;
    stripe_publishable_configured: boolean;
    stripe_webhook_secret_configured: boolean;
  };
  pricing: {
    pro_month_price_usd: number;
    pro_year_price_usd: number;
  };
  webhooks: {
    latest: {
      event_type: string;
      processing_status: string;
      received_at: string | null;
      processed_at: string | null;
      error_text: string | null;
    } | null;
    received_last_24h: number;
    failed_last_24h: number;
  };
  subscriptions: {
    active_subscription_rows: number;
    projected_pro_accounts: number;
  };
};

export type ApiCallOptions = {
  showError?: (message: string) => void;
  shouldShowError?: (error: ApiError) => boolean;
};

type ExecuteApiCallOptions = ApiCallOptions & {
  defaultErrorMessage: string;
};

function formatErrorForDisplay(error: ApiError): string {
  if (error.traceback) {
    return `${error.message}\n\nTraceback:\n${error.traceback}`;
  }

  return error.message;
}

async function parseApiError(response: Response, fallbackMessage: string): Promise<ApiError> {
  let errorBody: ApiErrorBody = {};
  let rawText = '';

  const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
  if (contentType.includes('application/json')) {
    errorBody = (await response.json().catch(() => ({}))) as ApiErrorBody;
  } else {
    rawText = (await response.text().catch(() => '')).trim();
    try {
      errorBody = (rawText ? JSON.parse(rawText) : {}) as ApiErrorBody;
      rawText = '';
    } catch {
      // keep rawText for non-JSON responses
    }
  }

  const code = errorBody.error || fallbackMessage;
  const message = errorBody.message || errorBody.error || rawText || fallbackMessage;

  return {
    status: response.status,
    code,
    message,
    traceback: errorBody.traceback,
  };
}

async function executeApiCall<T>(
  responsePromise: Promise<Response>,
  options: ExecuteApiCallOptions,
): Promise<ApiResult<T>> {
  const {
    defaultErrorMessage,
    showError,
    shouldShowError = () => true,
  } = options;

  try {
    const response = await responsePromise;

    if (!response.ok) {
      const parsedError = await parseApiError(response, defaultErrorMessage);

      if (showError && shouldShowError(parsedError)) {
        showError(formatErrorForDisplay(parsedError));
      }

      return { ok: false, error: parsedError };
    }

    const data = (await response.json()) as T;
    return { ok: true, data };
  } catch (error: unknown) {
    const parsedError = {
      status: 0,
      code: 'NETWORK_ERROR',
      message: error instanceof Error ? error.message : defaultErrorMessage,
    };

    if (showError && shouldShowError(parsedError)) {
      showError(parsedError.message);
    }

    return { ok: false, error: parsedError };
  }
}

export function createFreeAccount(options: ApiCallOptions = {}) {
  return executeApiCall<CreateFreeAccountResponse>(
    fetch('/api/create_free_account', {
      method: 'POST',
    }),
    {
      ...options,
      defaultErrorMessage: 'Failed to create account',
    }
  );
}

export function createDocument(request: CreateDocumentRequest, options: ApiCallOptions = {}) {
  return executeApiCall<CreateDocumentResponse>(
    authenticatedFetch('/api/create_doc', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }),
    {
      ...options,
      defaultErrorMessage: 'Failed to create document',
    }
  );
}

export function saveDocument(request: SaveDocumentRequest, options: ApiCallOptions = {}) {
  return executeApiCall<SaveDocumentResponse>(
    authenticatedFetch('/api/save_document', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }),
    {
      ...options,
      defaultErrorMessage: 'Failed to save document',
    }
  );
}

export function loadDocument(request: LoadDocumentRequest, options: ApiCallOptions = {}) {
  const params = new URLSearchParams({
    account_id: request.account_id,
  });

  if (request.anonymous_user_id) {
    params.set('anonymous_user_id', request.anonymous_user_id);
  }

  if (request.anonymous_account_id) {
    params.set('anonymous_account_id', request.anonymous_account_id);
  }

  return executeApiCall<LoadDocumentResponse>(
    authenticatedFetch(`/api/load_document/${encodeURIComponent(request.document_id)}?${params.toString()}`),
    {
      ...options,
      defaultErrorMessage: 'Failed to load document',
    }
  );
}

export function getDocuments(request: GetDocumentsRequest, options: ApiCallOptions = {}) {
  const params = new URLSearchParams({
    account_id: request.account_id,
  });

  if (request.anonymous_user_id) {
    params.set('anonymous_user_id', request.anonymous_user_id);
  }

  if (request.anonymous_account_id) {
    params.set('anonymous_account_id', request.anonymous_account_id);
  }

  return executeApiCall<GetDocumentsResponse>(
    authenticatedFetch(`/api/documents?${params.toString()}`),
    {
      ...options,
      defaultErrorMessage: 'Failed to fetch documents',
    }
  );
}

export function deleteDocument(documentId: string, options: ApiCallOptions = {}) {
  return executeApiCall<DeleteDocumentResponse>(
    authenticatedFetch(`/api/documents/${documentId}`, {
      method: 'DELETE',
    }),
    {
      ...options,
      defaultErrorMessage: 'Failed to delete document',
    }
  );
}

export function getTAccounts(request: GetTAccountsRequest, options: ApiCallOptions = {}) {
  const params = new URLSearchParams({
    account_id: request.account_id,
  });

  return executeApiCall<GetTAccountsResponse>(
    authenticatedFetch(`/api/taccounts?${params.toString()}`),
    {
      ...options,
      defaultErrorMessage: 'Failed to fetch taccounts',
    }
  );
}

export function createTAccount(request: CreateTAccountRequest, options: ApiCallOptions = {}) {
  return executeApiCall<TAccountRecord>(
    authenticatedFetch('/api/taccounts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        account_id: request.account_id,
        name: request.name,
        alias: request.alias,
      }),
    }),
    {
      ...options,
      defaultErrorMessage: 'Failed to create taccount',
    }
  );
}

export function updateTAccount(request: UpdateTAccountRequest, options: ApiCallOptions = {}) {
  const params = new URLSearchParams({
    account_id: request.account_id,
  });

  return executeApiCall<TAccountRecord>(
    authenticatedFetch(`/api/taccounts/${encodeURIComponent(request.taccount_id)}?${params.toString()}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        account_id: request.account_id,
        name: request.name,
        alias: request.alias,
      }),
    }),
    {
      ...options,
      defaultErrorMessage: 'Failed to update taccount',
    }
  );
}

export function deleteTAccount(taccountId: string, accountId: string, options: ApiCallOptions = {}) {
  const params = new URLSearchParams({
    account_id: accountId,
  });

  return executeApiCall<DeleteTAccountResponse>(
    authenticatedFetch(`/api/taccounts/${encodeURIComponent(taccountId)}?${params.toString()}`, {
      method: 'DELETE',
    }),
    {
      ...options,
      defaultErrorMessage: 'Failed to delete taccount',
    }
  );
}

export function sendFeedback(request: SendFeedbackRequest, options: ApiCallOptions = {}) {
  return executeApiCall<SendFeedbackResponse>(
    authenticatedFetch('/api/send_feedback', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }),
    {
      ...options,
      defaultErrorMessage: 'Failed to send feedback',
    }
  );
}

export function getUserSettings(request: UserSettingsRequest = {}, options: ApiCallOptions = {}) {
  const params = new URLSearchParams();

  if (request.account_id) {
    params.set('account_id', request.account_id);
  }

  if (request.anonymous_user_id) {
    params.set('anonymous_user_id', request.anonymous_user_id);
  }

  if (request.anonymous_account_id) {
    params.set('anonymous_account_id', request.anonymous_account_id);
  }

  const query = params.toString();
  const url = query ? `/api/user_settings?${query}` : '/api/user_settings';

  return executeApiCall<GetUserSettingsResponse>(
    authenticatedFetch(url),
    {
      ...options,
      defaultErrorMessage: 'Failed to fetch user settings',
    }
  );
}

export function saveUserSettings(request: SaveUserSettingsRequest, options: ApiCallOptions = {}) {
  return executeApiCall<SaveUserSettingsResponse>(
    authenticatedFetch('/api/user_settings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }),
    {
      ...options,
      defaultErrorMessage: 'Failed to save user settings',
    }
  );
}

export function getAdminStatus(options: ApiCallOptions = {}) {
  return executeApiCall<GetAdminStatusResponse>(
    authenticatedFetch('/api/access/admin-status'),
    {
      ...options,
      defaultErrorMessage: 'Failed to fetch admin status',
    }
  );
}

export function getRuntimeFlags(options: ApiCallOptions = {}) {
  return executeApiCall<GetRuntimeFlagsResponse>(
    authenticatedFetch('/api/config/flags'),
    {
      ...options,
      defaultErrorMessage: 'Failed to fetch runtime flags',
    }
  );
}

export function updateRuntimeFlags(request: UpdateRuntimeFlagsRequest, options: ApiCallOptions = {}) {
  return executeApiCall<UpdateRuntimeFlagsResponse>(
    authenticatedFetch('/api/config/flags', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }),
    {
      ...options,
      defaultErrorMessage: 'Failed to update runtime flags',
    }
  );
}

function buildBillingQuery(request: BillingPlansRequest = {}): string {
  const params = new URLSearchParams();

  if (request.account_id) {
    params.set('account_id', request.account_id);
  }

  if (request.anonymous_user_id) {
    params.set('anonymous_user_id', request.anonymous_user_id);
  }

  if (request.anonymous_account_id) {
    params.set('anonymous_account_id', request.anonymous_account_id);
  }

  const query = params.toString();
  return query ? `?${query}` : '';
}

export function getBillingPlans(request: BillingPlansRequest = {}, options: ApiCallOptions = {}) {
  return executeApiCall<BillingPlansResponse>(
    authenticatedFetch(`/api/billing/plans${buildBillingQuery(request)}`),
    {
      ...options,
      defaultErrorMessage: 'Failed to fetch billing plans',
    }
  );
}

export function getBillingStatus(request: BillingStatusRequest = {}, options: ApiCallOptions = {}) {
  return executeApiCall<BillingStatusResponse>(
    authenticatedFetch(`/api/billing/status${buildBillingQuery(request)}`),
    {
      ...options,
      defaultErrorMessage: 'Failed to fetch billing status',
    }
  );
}

export function createBillingCheckoutSession(
  request: CreateBillingCheckoutSessionRequest,
  options: ApiCallOptions = {},
) {
  return executeApiCall<CreateBillingCheckoutSessionResponse>(
    authenticatedFetch('/api/billing/checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }),
    {
      ...options,
      defaultErrorMessage: 'Failed to start checkout',
    }
  );
}

export function validateDiscountCode(code: string, options: ApiCallOptions = {}) {
  return executeApiCall<ValidateDiscountCodeResponse>(
    fetch('/api/billing/discount-code/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    }),
    { ...options, defaultErrorMessage: 'Invalid discount code' }
  );
}

export function cancelBillingSubscription(
  request: CancelBillingSubscriptionRequest = {},
  options: ApiCallOptions = {},
) {
  return executeApiCall<CancelBillingSubscriptionResponse>(
    authenticatedFetch('/api/billing/cancel-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }),
    {
      ...options,
      defaultErrorMessage: 'Failed to cancel plan',
    }
  );
}

export function getBillingDiagnostics(options: ApiCallOptions = {}) {
  return executeApiCall<BillingDiagnosticsResponse>(
    authenticatedFetch('/api/billing/diagnostics'),
    {
      ...options,
      defaultErrorMessage: 'Failed to fetch billing diagnostics',
    }
  );
}

export function getAdminPlans(options: ApiCallOptions = {}) {
  return executeApiCall<GetAdminPlansResponse>(
    authenticatedFetch('/api/config/plans'),
    {
      ...options,
      defaultErrorMessage: 'Failed to fetch admin plans',
    }
  );
}

export function updateAdminPlans(request: UpdateAdminPlansRequest, options: ApiCallOptions = {}) {
  return executeApiCall<UpdateAdminPlansResponse>(
    authenticatedFetch('/api/config/plans', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }),
    {
      ...options,
      defaultErrorMessage: 'Failed to update admin plans',
    }
  );
}

export function getAdminDiscountCodes(options: ApiCallOptions = {}) {
  return executeApiCall<AdminDiscountCodesResponse>(
    authenticatedFetch('/api/access/discount-codes'),
    { ...options, defaultErrorMessage: 'Failed to fetch discount codes' }
  );
}

export function createAdminDiscountCode(request: AdminDiscountCodeRequest, options: ApiCallOptions = {}) {
  return executeApiCall<{ discount_code_id: number }>(
    authenticatedFetch('/api/access/discount-codes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    }),
    { ...options, defaultErrorMessage: 'Failed to create discount code' }
  );
}

export function updateAdminDiscountCode(
  codeId: number,
  request: AdminDiscountCodeRequest,
  options: ApiCallOptions = {},
) {
  return executeApiCall<{ discount_code_id: number; expired_checkout_sessions: number }>(
    authenticatedFetch(`/api/access/discount-codes/${codeId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    }),
    { ...options, defaultErrorMessage: 'Failed to update discount code' }
  );
}

export function deleteAdminDiscountCode(codeId: number, options: ApiCallOptions = {}) {
  return executeApiCall<{ discount_code_id: number; expired_checkout_sessions: number }>(
    authenticatedFetch(`/api/access/discount-codes/${codeId}`, { method: 'DELETE' }),
    { ...options, defaultErrorMessage: 'Failed to archive discount code' }
  );
}

export function getAdminCommissions(options: ApiCallOptions = {}) {
  return executeApiCall<AdminCommissionsResponse>(
    authenticatedFetch('/api/access/commissions'),
    { ...options, defaultErrorMessage: 'Failed to fetch commissions' }
  );
}

export function settleAdminCommissions(
  contentProviderId: number,
  unpaidCommissionIds: number[],
  options: ApiCallOptions = {},
) {
  return executeApiCall<AdminCommissionPaymentBatch>(
    authenticatedFetch('/api/access/commissions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        content_provider_id: contentProviderId,
        unpaid_commission_ids: unpaidCommissionIds,
      }),
    }),
    { ...options, defaultErrorMessage: 'Failed to mark commissions paid' }
  );
}

export function getAdminUsers(
  page: number,
  exportAll = false,
  showUnnamed = false,
  options: ApiCallOptions = {},
) {
  const params = new URLSearchParams({ page: String(page) });
  if (exportAll) {
    params.set('export', 'true');
  }
  params.set('show_unnamed', String(showUnnamed));

  return executeApiCall<GetAdminUsersResponse>(
    authenticatedFetch(`/api/access/users?${params.toString()}`),
    {
      ...options,
      defaultErrorMessage: 'Failed to fetch users',
    }
  );
}

export function deleteAdminUser(userId: string, options: ApiCallOptions = {}) {
  return executeApiCall<DeleteAdminUserResponse>(
    authenticatedFetch(`/api/access/users/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    }),
    {
      ...options,
      defaultErrorMessage: 'Failed to delete user',
    }
  );
}
